import { InjectionToken } from '@angular/core';

/** A recognised and translated span of speech. */
export interface TranslationSegment {
  /** Stable across partial updates, so a caption can be revised in place. */
  id: string;
  /** What was said, in the speaker's language. */
  sourceText: string;
  /** What it means, in the listener's language. */
  translatedText: string;
  sourceLanguage: string;
  targetLanguage: string;
  /**
   * False while the recogniser is still revising this span.
   *
   * Partials are what make captions feel immediate, but only a final segment
   * should be treated as what was actually said — a clinician must not act on a
   * half-recognised dosage.
   */
  final: boolean;
}

export interface TranslationSessionOptions {
  sourceLanguage: string;
  targetLanguage: string;
  /**
   * The microphone track, and only ever the microphone track.
   *
   * §8 is explicit that the tap is never fed from the stethoscope: sending
   * heart sounds to a speech recogniser is pointless, and the round trip would
   * be clinically unacceptable if it were ever in the audio path.
   */
  track: MediaStreamTrack;
  onSegment(segment: TranslationSegment): void;
  onError(message: string): void;
  /**
   * Periodic health from the capture tap, where a provider has one.
   *
   * Optional because not every provider taps audio itself. It exists because
   * "no captions" has several causes that look identical from the outside — a
   * dead tap, a quiet room, an engine returning nothing — and only the tap can
   * tell the first from the second (§9.3).
   */
  onStats?(stats: TranslationTapStats): void;
}

export interface TranslationTapStats {
  /** Frames handed to the transport since the last report. */
  framesPosted: number;
  /** Quanta with no signal above the noise floor. */
  silentQuanta: number;
  /** The context's actual rate, which may not be the one requested. */
  sampleRate: number;
}

export interface TranslationSession {
  stop(): Promise<void>;
}

export interface LanguageOption {
  /** BCP-47, e.g. 'hi-IN'. */
  code: string;
  label: string;
}

/**
 * What a provider can do, for the UI to render from (§8.3).
 *
 * This belongs to the provider rather than to configuration because whether a
 * language works is a fact about the deployed model. A picker built from a
 * hardcoded list keeps offering languages after the model behind it changed,
 * which is the same failure as a control that is always visible and never
 * works.
 *
 * A provider that learns this from a server reports empty lists until its first
 * session has connected. The UI must therefore treat "no languages yet" as
 * *unknown*, not as *none* — and in particular must not conclude the provider is
 * broken before it has been asked to start.
 */
export interface TranslationCapabilities {
  sourceLanguages: readonly LanguageOption[];
  /**
   * May be narrower than `sourceLanguages`. Whisper's translate task is
   * English-target only, so an engine can recognise many languages while
   * translating into just one.
   */
  targetLanguages: readonly LanguageOption[];
  /**
   * False for a batch engine, which emits only finals.
   *
   * Carried so the UI can be honest: where this is false, no caption will ever
   * be revised, and nothing should render as though a revision is still coming.
   */
  emitsPartials: boolean;
}

/** No languages, no partials — what an unavailable or unstarted provider reports. */
export const NO_CAPABILITIES: TranslationCapabilities = {
  sourceLanguages: [],
  targetLanguages: [],
  emitsPartials: false,
};

/**
 * Speech recognition and translation, behind an interface.
 *
 * The boundary exists so a provider can be swapped without touching the audio
 * layer — and, more importantly, so that the rule "translation never sits in
 * the audio path" is enforced by shape rather than by discipline. A provider
 * receives a *copy* of the microphone track and returns text. It is given no
 * way to insert itself into what is transmitted.
 */
export interface TranslationProvider {
  readonly name: string;
  /** False when the provider has no credentials or is otherwise unusable. */
  isAvailable(): boolean;
  /**
   * What this provider can actually do, for the UI to render from.
   *
   * Empty until a session has run, for a provider that learns its capabilities
   * from a server — see `TranslationCapabilities`.
   */
  capabilities(): TranslationCapabilities;
  start(options: TranslationSessionOptions): Promise<TranslationSession>;
}

export const TRANSLATION_PROVIDER = new InjectionToken<TranslationProvider>(
  'TRANSLATION_PROVIDER',
);

/**
 * Stands in when no provider is configured.
 *
 * Reports unavailable rather than pretending to work. §8 makes translation
 * strictly additive, so an absent provider must disable captions with a visible
 * notice and leave the consultation otherwise untouched.
 */
export class UnavailableTranslationProvider implements TranslationProvider {
  readonly name = 'none';

  isAvailable(): boolean {
    return false;
  }

  capabilities(): TranslationCapabilities {
    return NO_CAPABILITIES;
  }

  async start(): Promise<TranslationSession> {
    throw new Error('no translation provider is configured');
  }
}
